import React, { useContext, useEffect, useRef, useState } from "react";
import cl from "classnames";
import css from "./EnquiryFormMessageArea.module.scss";
import { useFormContext } from "react-hook-form";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { EnquiryFormMessageType } from "./types";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import { EnquiryFormContext } from "./EnquiryForm";

interface Props {
    tags: string[];
    suggestions: Record<string, string>;
    defaultSuggestion: string;
}

function EnquiryFormMessageArea({
    tags,
    suggestions,
    defaultSuggestion,
}: Props) {
    const { listenSpecialEventClick } = useContext(EnquiryFormContext);
    const { register, getValues, setValue } = useFormContext<EnquiryFormMessageType>() ;
    const [placeholder, setPlaceholder] = useState<string>("Your message");

    const [selectedTagsIds, setSelectedTagsIds] = useState<
        Record<string, boolean>
    >({});

    const getUsersCustomText = (): string => {
        // Get the current message value
        const currentMessage = getValues("message") || "";

        // Remove all text that matches any suggestion
        const updatedMessage = Object.values(suggestions).reduce((message, suggestion) => {
            const escapedSuggestion = suggestion.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special regex characters
            const regex = new RegExp(`(^|\\s)${escapedSuggestion}(\\s|$)`, "g"); // Match whole words with surrounding spaces or start/end of string
            return message.replace(regex, " ").replace(/\s+/g, " ").trim(); // Replace matches and normalize spaces
        }, currentMessage);
    
        return updatedMessage;
    };
    
    const onSelectTag = (idx: number, selected: boolean) => {
        let ids = { ...selectedTagsIds };
        if (selected) {
            ids[idx] = true;
        } else {
            delete ids[idx];
        }

        setSelectedTagsIds(ids);

        const currentMessage = getUsersCustomText();
        const suggestedMessage = suggestions[Object.keys(ids).join(",")] ?? defaultSuggestion;
        const updatedMessage = currentMessage.trim()
            ? `${currentMessage} ${suggestedMessage}`
            : suggestedMessage;

        setValue("message", updatedMessage);
    };

    useEffect(() => {
        if(!listenSpecialEventClick) {
            return;
        }
        const abort = new AbortController();
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.tagName === "A" && target.dataset.enquiryFormHook) {
                const hook = target.dataset.enquiryFormHook;
                setPlaceholder(hook);
                ref.current?.focus();
            }
        }, { signal: abort.signal });
        return () => abort.abort();
    }, [defaultSuggestion]);
    const ref = useRef<HTMLTextAreaElement>(null);

    return (
        <div className={css.container}>
            <Form.Control
                as="textarea"
                rows={13}
                className="p-3"
                data-testid="enquiry-form-message"
                placeholder={placeholder}
                {...register("message")}
            />
            <div className={css.tags}>
                {tags.map((tag, idx) => (
                    <label
                        key={idx}
                        className={cl(css.tag, {
                            [css.selected]: selectedTagsIds[idx],
                        })}
                    >
                        <Icon icon={selectedTagsIds[idx] ? faMinus : faPlus} className={css.tagPrefix}/>
                        {tag}
                        <input
                            type="checkbox"
                            data-testid={`enquiry-form-suggestion-${idx}`}
                            onChange={(e) => {
                                onSelectTag(idx, e.target.checked);
                            }}
                        ></input>
                    </label>
                ))}
            </div>
        </div>
    );
}

export default EnquiryFormMessageArea;
